import React, { useState } from 'react';
import { ICampaignDetails } from '../../interfaces/campaign.interface';
import { Button, Spinner } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../../store/store';
import { getActiveBrand } from '../../store/selector/brand.selector';
import { SetSelectedCampaign } from '../../store/slice/campaign.slice';
import { useCreateNewSessionMutation } from '../../store/api/session.api';
import { ISession } from '../../interfaces/session.interface';
import { AddSession, SetCurrentlyWorkingSession } from '../../store/slice/session.slice';
import { useNavigate } from 'react-router-dom';
import { getSessions } from '../../store/selector/session.selector';
import { getEmptySession } from '../../utils/session.utils';

interface IProps {
    campaign: ICampaignDetails;
    selectingPlanId: string | null;
    setSelectingPlanId: React.Dispatch<React.SetStateAction<string | null>>;
}

const DashboardCampaignPlan = ({ campaign, selectingPlanId, setSelectingPlanId }: IProps) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const activeBrand = useAppSelector(getActiveBrand);
    const sessions = useAppSelector(getSessions);
    const [createNewSession] = useCreateNewSessionMutation();

    const handlePlanSelect = async () => {
        if (!activeBrand || selectingPlanId !== null) return;
        setSelectingPlanId(campaign.id);
        dispatch(SetSelectedCampaign(null));
        let session = getEmptySession(sessions);
        if (!session) {
            const response = await createNewSession(activeBrand.id);
            if (response && 'data' in response) {
                session = response.data;
                dispatch(AddSession(session));
            }
        }
        if (session) {
            dispatch(SetCurrentlyWorkingSession(session));
            dispatch(SetSelectedCampaign(campaign));
            navigate(`/campaign?q=${session.sessionKey}`);
        }
        setSelectingPlanId(null);
    };

    return (
        <div className="tw-w-[328px] tw-p-4 tw-border tw-rounded-lg">
            <div className="tw-font-semibold tw-text-purple-100 tw-overflow-hidden tw-whitespace-nowrap tw-text-ellipsis">
                {campaign.name}
            </div>
            <div className="tw-flex tw-items-center tw-justify-between tw-my-4">
                <div className="tw-relative tw-w-[102px] tw-h-[102px]">
                    {/* Bottom-Left Box */}
                    <div className="tw-absolute tw-w-[80.53px] tw-h-[80.53px] tw-bg-[#F4F4F4] tw-rounded-lg tw-top-[18px] tw-left-[0px] tw-z-20"></div>

                    {/* Middle Box (Centered) */}
                    <div className="tw-absolute tw-w-[80.53px] tw-h-[80.53px] tw-bg-[#E3E3E3] tw-rounded-lg tw-top-[9px] tw-left-[9px] tw-z-10"></div>

                    {/* Top-Right Box */}
                    <div className="tw-absolute tw-w-[80.53px] tw-h-[80.53px] tw-bg-[#CBCBCB] tw-rounded-lg tw-top-[0px] tw-left-[18px] tw-z-0"></div>
                </div>

                <div className="tw-flex tw-flex-col tw-items-start tw-justify-between tw-h-[102px]">
                    <div>
                        <div className="tw-text-purple-100 tw-font-semibold">Objectives</div>
                        <div className="tw-w-[168px] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm">
                            {campaign.objective}
                        </div>
                    </div>
                    <div>
                        <div className="tw-text-purple-100 tw-font-semibold">Target Audience</div>
                        <div className="tw-w-[168px] tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap tw-text-sm">
                            {campaign.target_audience}
                        </div>
                    </div>
                </div>
            </div>
            <Button
                className="tw-bg-purple-100 tw-w-full"
                onClick={handlePlanSelect}
                disabled={selectingPlanId === campaign.id}
            >
                <span>Select Plan</span>
                {selectingPlanId === campaign.id && (
                    <Spinner className="tw-text-white tw-w-6 tw-h-6 tw-ml-2" />
                )}
            </Button>
        </div>
    );
};

export default DashboardCampaignPlan;
